import type { ReactNode } from 'react'
import {
  type Control,
  Controller,
  type FieldErrors,
  type FieldPath,
  get,
  type UseFormRegister,
} from 'react-hook-form'
import type { MaskedInputProps } from 'react-text-mask'

import AtomCheckbox from '@/components/Atoms/AtomCheckbox/AtomCheckbox'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import type {
  FormInputControlType,
  FormInputNameType,
  FormInputRegisterType,
  OptionType,
} from '@/types'

import BooleanSelect from '../ConfigFields/BooleanSelect'
import type { ConfigPriceFormValues } from '../lib/configPriceModel'

export type PriceFieldContext = {
  control: Control<ConfigPriceFormValues>
  register: UseFormRegister<ConfigPriceFormValues>
  errors: FieldErrors<ConfigPriceFormValues>
  isOrganisationSelected: boolean
}

type PriceTextFieldProps = {
  context: PriceFieldContext
  name: FormInputNameType & FieldPath<ConfigPriceFormValues>
  title: ReactNode
  placeholder?: string
  mask?: MaskedInputProps['mask']
  error?: string
}

export function PriceTextField({
  context,
  name,
  title,
  placeholder,
  mask,
  error,
}: PriceTextFieldProps) {
  return (
    <AtomFormInput
      layout="stacked"
      idPrefix="config-price-"
      location="stockImportConfig"
      isOrganisationSelected={context.isOrganisationSelected}
      name={name}
      title={title}
      placeholder={placeholder}
      mask={mask}
      inputMode={mask ? 'numeric' : 'decimal'}
      autoComplete="off"
      error={error}
      errors={context.errors}
      register={context.register as FormInputRegisterType}
      control={context.control as FormInputControlType}
    />
  )
}

type PriceSelectFieldProps = {
  context: PriceFieldContext
  name: FieldPath<ConfigPriceFormValues>
  title: ReactNode
  options: OptionType[]
  placeholder?: string
  loading?: boolean
}

export function PriceSelectField({
  context,
  name,
  title,
  options,
  placeholder,
  loading,
}: PriceSelectFieldProps) {
  const error: { message?: string } | undefined = get(context.errors, name)

  return (
    <Controller
      control={context.control}
      name={name}
      render={({ field }) => (
        <HoverElement>
          <AtomSelect
            layout="stacked"
            outlined
            size="large"
            valueMode="value"
            openByHover={false}
            showSearch
            isOrganisationSelected={context.isOrganisationSelected}
            title={title}
            placeholder={placeholder}
            options={options}
            loading={loading}
            value={
              typeof field.value === 'string' && field.value !== ''
                ? field.value
                : undefined
            }
            handleChange={(value) => {
              if (typeof value === 'string') field.onChange(value)
            }}
            handleClear={() => field.onChange('')}
            error={error?.message ?? ''}
          />
        </HoverElement>
      )}
    />
  )
}

type PriceCheckboxFieldProps = {
  context: PriceFieldContext
  name: FieldPath<ConfigPriceFormValues>
  children: ReactNode
}

export function PriceCheckboxField({
  context,
  name,
  children,
}: PriceCheckboxFieldProps) {
  return (
    <Controller
      control={context.control}
      name={name}
      render={({ field }) => (
        <AtomCheckbox
          checked={field.value === true}
          onChange={field.onChange}
          isOrganisationSelected={context.isOrganisationSelected}
        >
          {children}
        </AtomCheckbox>
      )}
    />
  )
}

type PriceBooleanSelectFieldProps = {
  context: PriceFieldContext
  name: FieldPath<ConfigPriceFormValues>
  title: ReactNode
  trueLabel: string
  falseLabel: string
}

export function PriceBooleanSelectField({
  context,
  name,
  ...labels
}: PriceBooleanSelectFieldProps) {
  return (
    <Controller
      control={context.control}
      name={name}
      render={({ field }) => (
        <BooleanSelect
          {...labels}
          value={field.value === true}
          onChange={field.onChange}
          isOrganisationSelected={context.isOrganisationSelected}
        />
      )}
    />
  )
}
